import React, { useRef, useEffect, memo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  LayoutGrid,
  BarChart2,
  GitCompare,
  BarChart3,
  TrendingUp,
  ScatterChart,
  Grid3x3,
  AlignHorizontalDistributeCenter,
  ArrowUpNarrowWide,
  LayoutDashboard,
  Table2,
  Building2,
  Landmark,
  LineChart,
  Activity,
  Percent,
  Currency,
  Gem,
  Globe,
  Circle,
  Folder,
  Layers,
  Database,
  Cpu,
  Box,
  Coins,
  Banknote,
  Scale,
  Blocks,
  Bitcoin,
  BadgeDollarSign,
  Shapes,
  type LucideIcon,
} from "lucide-react";
import { type NavItem } from "../navbar/navbarConfig";
import { CATEGORIES } from "@/constants/sectors/onChainPage";
import { getIconForLabel } from "@/config/icons";

// Static icon map for flyout to avoid DynamicIcon performance issues
const FLYOUT_ICONS: Record<string, LucideIcon> = {
  // Layout & Navigation
  "layout-grid": LayoutGrid,
  "layout-dashboard": LayoutDashboard,
  "folder": Folder,
  "circle": Circle,

  // Charts
  "bar-chart-2": BarChart2,
  "bar-chart-3": BarChart3,
  "line-chart": LineChart,
  "scatter-chart": ScatterChart,
  "trending-up": TrendingUp,
  "table-2": Table2,
  "grid-3x3": Grid3x3,
  "align-horizontal-distribute-center": AlignHorizontalDistributeCenter,
  "arrow-up-narrow-wide": ArrowUpNarrowWide,

  // Comparison & Analysis
  "git-compare": GitCompare,
  "activity": Activity,
  "percent": Percent,

  // Financial & Economic
  "building-2": Building2,
  "landmark": Landmark,
  "currency": Currency,
  "gem": Gem,
  "globe": Globe,
  "coins": Coins,
  "banknote": Banknote,
  "scale": Scale,
  "badge-dollar-sign": BadgeDollarSign,

  // Sector Icons
  "layers": Layers,
  "database": Database,
  "cpu": Cpu,
  "box": Box,
  "blocks": Blocks,
  "bitcoin": Bitcoin,
  "shapes": Shapes,
};

// Get static icon component
const getStaticIcon = (name: string): LucideIcon => {
  return FLYOUT_ICONS[name] || Circle;
};

// Map category color names to Tailwind text classes
const getColorClass = (color: string): string => {
  const colorMap: Record<string, string> = {
    purple: "text-purple-400",
    green: "text-green-400",
    pink: "text-pink-400",
    indigo: "text-indigo-400",
    cyan: "text-cyan-400",
    emerald: "text-emerald-400",
    orange: "text-orange-400",
    blue: "text-blue-400",
    red: "text-red-400",
    yellow: "text-yellow-400",
  };
  return colorMap[color] || "text-primary";
};

interface SidebarFlyoutProps {
  item: NavItem;
  position: { top: number };
  sidebarWidth: number;
  onClose: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

// Sector category order for display - split into three columns
const SECTOR_ORDER_COL1 = [
  "core-infrastructure",
  "defi",
  "stablecoins",
];

const SECTOR_ORDER_COL2 = [
  "emerging",
  "assets",
];

const SECTOR_ORDER_COL3 = [
  "infrastructure",
  "bitcoin-ecosystem",
  "cex-tokens",
  "miscellaneous",
];

const SidebarFlyout: React.FC<SidebarFlyoutProps> = ({
  item,
  position,
  sidebarWidth,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => {
  const navigate = useNavigate();
  const flyoutRef = useRef<HTMLDivElement>(null);

  // Adjust position if flyout would go off-screen
  useEffect(() => {
    if (flyoutRef.current) {
      const rect = flyoutRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      if (rect.bottom > viewportHeight - 20) {
        const newTop = Math.max(60, viewportHeight - rect.height - 20);
        flyoutRef.current.style.top = `${newTop}px`;
      }
    }
  }, [position]);

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  // Render navigation list for children
  const renderNavList = (children: NavItem[]) => (
    <div className="space-y-0.5">
      {children.map((child) => {
        const IconComponent = getStaticIcon(getIconForLabel(child.label) || "circle");
        return (
          <button
            key={child.path}
            onClick={() => handleNavigate(child.path)}
            className="w-full flex items-start gap-2.5 px-3 py-2 rounded-md text-left
                       text-white/70 hover:text-white hover:bg-white/5 transition-colors"
          >
            <IconComponent className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/40" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium">{child.label}</div>
              {child.description && (
                <p className="text-xs text-white/40 mt-0.5 leading-tight">{child.description}</p>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );

  const renderSectorColumn = (sectorKeys: string[]) => (
    <div className="space-y-3 flex-1 min-w-0">
      {sectorKeys.map((key) => {
        const cat = CATEGORIES[key];
        if (!cat) return null;

        const hasTabs = cat.tabs && cat.tabs.length > 0;
        const basePath = `/app/${cat.route}`;
        const iconName = cat.iconName || "folder";
        // Use cardDescription for shorter text, fallback to description
        const shortDesc = cat.cardDescription || cat.description;
        const iconColorClass = getColorClass(cat.color);
        const CategoryIcon = getStaticIcon(iconName);

        return (
          <div key={key} className="mb-2">
            <button
              onClick={() => handleNavigate(basePath)}
              className="w-full px-2 py-1.5 rounded-md text-left
                         text-white/90 hover:text-white hover:bg-white/5 transition-colors group"
            >
              <div className="flex items-center gap-2">
                <CategoryIcon className={`w-4 h-4 flex-shrink-0 ${iconColorClass}`} />
                <span className="text-sm font-semibold">{cat.title}</span>
              </div>
              {shortDesc && (
                <p className="text-xs text-white/40 mt-0.5 leading-tight line-clamp-2 ml-6">
                  {shortDesc}
                </p>
              )}
            </button>

            {hasTabs && (
              <div className="ml-6 mt-1.5 space-y-1 border-l border-white/10 pl-2">
                {cat.tabs.map((tab) => {
                  const tabPath = `${basePath}/${tab.id}`;
                  const hasDropdown = cat.dropdowns && cat.dropdowns[tab.id];
                  const dropdownItems = hasDropdown ? cat.dropdowns![tab.id] : undefined;
                  const TabIcon = getStaticIcon(tab.iconName || "circle");

                  return (
                    <div key={tab.id}>
                      <button
                        onClick={() => handleNavigate(tabPath)}
                        className="w-full px-1.5 py-1 text-left
                                   text-white/60 hover:text-white transition-colors rounded"
                      >
                        <div className="flex items-center gap-1.5">
                          <TabIcon className="w-3.5 h-3.5 flex-shrink-0 text-white/40" />
                          <span className="text-sm">{tab.label}</span>
                        </div>
                      </button>
                      {dropdownItems && dropdownItems.length > 0 && (
                        <div className="ml-5 mt-0.5 space-y-0.5 border-l border-white/5 pl-2">
                          {dropdownItems.map((subItem) => {
                            const SubIcon = getStaticIcon(subItem.iconName || "circle");
                            return (
                              <button
                                key={subItem.id}
                                onClick={() => handleNavigate(`${tabPath}/${subItem.id}`)}
                                className="w-full flex items-center gap-1.5 text-left px-1.5 py-0.5
                                           text-white/50 hover:text-white transition-colors rounded text-sm"
                              >
                                <SubIcon className="w-3 h-3 flex-shrink-0 text-white/30" />
                                <span className="truncate">{subItem.label}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderSectorTree = () => {
    return (
      <>
        {/* View All Sectors link at top */}
        <div className="mb-4 px-3">
          <button
            onClick={() => handleNavigate("/app/sectors")}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md
                       bg-white/5 hover:bg-white/10 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <LayoutGrid className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-white">View All Sectors</span>
            </div>
            <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
          </button>
        </div>

        <div className="flex gap-4 px-2">
          {renderSectorColumn(SECTOR_ORDER_COL1)}
          {renderSectorColumn(SECTOR_ORDER_COL2)}
          {renderSectorColumn(SECTOR_ORDER_COL3)}
        </div>
      </>
    );
  };

  // Enhanced Charts flyout with grouped chart types
  const renderChartsContent = () => {
    const chartGroups = [
      {
        title: "Comparison",
        color: "text-blue-400",
        icon: "git-compare",
        charts: [
          { label: "Scatter", path: "/app/tokens/charts/scatter", description: "Plot tokens on X/Y axes", icon: "scatter-chart" },
          { label: "Correlation", path: "/app/tokens/charts/correlation", description: "Cross-token relationships", icon: "grid-3x3" },
          { label: "Parallel", path: "/app/tokens/charts/parallel", description: "Multi-metric comparison", icon: "align-horizontal-distribute-center" },
        ],
      },
      {
        title: "Distribution",
        color: "text-emerald-400",
        icon: "bar-chart-3",
        charts: [
          { label: "Ranking", path: "/app/tokens/charts/distribution", description: "Sort by any metric", icon: "arrow-up-narrow-wide" },
          { label: "Treemap", path: "/app/tokens/charts/treemap", description: "Hierarchical market cap", icon: "layout-grid" },
        ],
      },
      {
        title: "Time Series",
        color: "text-purple-400",
        icon: "trending-up",
        charts: [
          { label: "Heatmap", path: "/app/tokens/charts/heatmap", description: "Performance over time", icon: "table-2" },
        ],
      },
    ];

    return (
      <>
        {/* Quick access button */}
        <div className="mb-4 px-3">
          <button
            onClick={() => handleNavigate("/app/tokens/charts")}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md
                       bg-white/5 hover:bg-white/10 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-white">Open Charts Explorer</span>
            </div>
            <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
          </button>
        </div>

        {/* Chart groups */}
        <div className="space-y-4 px-2">
          {chartGroups.map((group) => {
            const GroupIcon = getStaticIcon(group.icon);
            return (
              <div key={group.title}>
                <div className="flex items-center gap-2 px-2 mb-2">
                  <GroupIcon className={`w-4 h-4 ${group.color}`} />
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/50">
                    {group.title}
                  </span>
                </div>
                <div className="space-y-0.5">
                  {group.charts.map((chart) => {
                    const ChartIcon = getStaticIcon(chart.icon || "circle");
                    return (
                      <button
                        key={chart.path}
                        onClick={() => handleNavigate(chart.path)}
                        className="w-full flex items-start gap-2.5 px-3 py-2 rounded-md text-left
                                   text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <ChartIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/40" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{chart.label}</div>
                          <p className="text-xs text-white/40 mt-0.5 leading-tight">{chart.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // Enhanced Macro flyout with grouped data categories
  const renderMacroContent = () => {
    const macroGroups = [
      {
        title: "Economic Data",
        color: "text-blue-400",
        icon: "building-2",
        items: [
          { label: "Overview", path: "/app/macro", description: "Key metrics at a glance", icon: "layout-dashboard" },
          { label: "Major Indicators", path: "/app/macro/major-indicators", description: "GDP, inflation, employment", icon: "activity" },
        ],
      },
      {
        title: "Central Banks",
        color: "text-amber-400",
        icon: "landmark",
        items: [
          { label: "Fed Probabilities", path: "/app/macro/fed-probabilities", description: "Rate decision forecasts", icon: "percent" },
        ],
      },
      {
        title: "Markets",
        color: "text-emerald-400",
        icon: "line-chart",
        items: [
          { label: "Correlations", path: "/app/macro/correlations", description: "Cross-asset relationships", icon: "git-compare" },
          { label: "Foreign Exchange", path: "/app/macro/fx", description: "Currency pairs & DXY", icon: "currency" },
          { label: "Commodities", path: "/app/macro/commodities", description: "Gold, oil, and more", icon: "gem" },
        ],
      },
    ];

    return (
      <>
        {/* Quick access button */}
        <div className="mb-4 px-3">
          <button
            onClick={() => handleNavigate("/app/macro")}
            className="w-full flex items-center justify-between px-3 py-2 rounded-md
                       bg-white/5 hover:bg-white/10 transition-colors group"
          >
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium text-white">Open Macro Dashboard</span>
            </div>
            <ArrowRight className="w-4 h-4 text-white/50 group-hover:text-white group-hover:translate-x-0.5 transition-all" />
          </button>
        </div>

        {/* Macro groups */}
        <div className="space-y-4 px-2">
          {macroGroups.map((group) => {
            const GroupIcon = getStaticIcon(group.icon);
            return (
              <div key={group.title}>
                <div className="flex items-center gap-2 px-2 mb-2">
                  <GroupIcon className={`w-4 h-4 ${group.color}`} />
                  <span className="text-xs font-semibold uppercase tracking-wider text-white/50">
                    {group.title}
                  </span>
                </div>
                <div className="space-y-0.5">
                  {group.items.map((item) => {
                    const ItemIcon = getStaticIcon(item.icon || "circle");
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigate(item.path)}
                        className="w-full flex items-start gap-2.5 px-3 py-2 rounded-md text-left
                                   text-white/70 hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <ItemIcon className="w-4 h-4 mt-0.5 flex-shrink-0 text-white/40" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{item.label}</div>
                          <p className="text-xs text-white/40 mt-0.5 leading-tight">{item.description}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const renderContent = () => {
    const { flyoutType, children } = item;

    switch (flyoutType) {
      case "charts":
        return renderChartsContent();

      case "sectors":
        return renderSectorTree();

      case "macro":
        return renderMacroContent();

      default:
        return (
          <>
            {item.description && (
              <p className="text-sm text-white/50 mb-3 px-3">{item.description}</p>
            )}
            {children && renderNavList(children)}
          </>
        );
    }
  };

  // Determine width based on flyout type
  const getFlyoutWidth = () => {
    switch (item.flyoutType) {
      case "sectors":
        return "w-[850px]";
      case "charts":
      case "macro":
        return "w-80";
      default:
        return "w-72";
    }
  };
  const flyoutWidth = getFlyoutWidth();

  return (
    <div
      ref={flyoutRef}
      className={`fixed z-[60] bg-gray-900 border border-white/10 rounded-lg shadow-2xl shadow-black/50 ${flyoutWidth} py-3
                 animate-in fade-in slide-in-from-left-2 duration-150`}
      style={{
        left: `${sidebarWidth + 8}px`,
        top: `${Math.max(position.top, 70)}px`,
        maxHeight: "calc(100vh - 100px)",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3 pb-3 border-b border-white/10 px-3">
        {(() => {
          const HeaderIcon = getStaticIcon(getIconForLabel(item.label) || "folder");
          return <HeaderIcon className="w-5 h-5 text-primary" />;
        })()}
        <h3 className="text-base font-semibold text-white flex-1">{item.label}</h3>
      </div>

      {/* Content - scrollable area */}
      <div className="overflow-y-auto max-h-[calc(100vh-200px)] px-1 custom-scrollbar">
        {renderContent()}
      </div>
    </div>
  );
};

export default memo(SidebarFlyout);
